import { useState } from "react";
import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import NotificationBell from "@/components/NotificationBell";
import { useSeekerAuth } from "@/context/SeekerAuthContext";
import { useBranding, BrandLogo } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Briefcase, FileSpreadsheet, MessageSquare, LogOut, User, UserCircle, Bookmark, Menu, Calendar } from "lucide-react";

const NAV = [
    { to: "/seeker/jobs", label: "Browse jobs", icon: Briefcase },
    { to: "/seeker/applications", label: "My applications", icon: FileSpreadsheet },
    { to: "/seeker/interviews", label: "Interviews", icon: Calendar },
    { to: "/seeker/saved", label: "Saved", icon: Bookmark },
    { to: "/seeker/messages", label: "Messages", icon: MessageSquare },
    { to: "/seeker/profile", label: "Profile", icon: UserCircle },
];

export default function SeekerShell({ children, requireAuth = true }) {
    const { seeker, loading, logout } = useSeekerAuth();
    const branding = useBranding();
    const nav = useNavigate();
    const loc = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-sm text-muted-foreground font-mono">loading...</div>
            </div>
        );
    }
    if (requireAuth && !seeker) {
        return <Navigate to="/seeker/login" replace />;
    }

    const handleLogout = async () => {
        await logout();
        nav("/seeker/login");
    };

    return (
        <div className="min-h-screen bg-background flex flex-col">
            <header className="border-b border-border bg-white sticky top-0 z-30">
                <div className="flex items-center h-14 sm:h-16 px-3 sm:px-6 gap-3 sm:gap-8">
                    {seeker && (
                        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                            <SheetTrigger asChild>
                                <button
                                    type="button"
                                    className="md:hidden p-2 -ml-2 hover:bg-secondary"
                                    data-testid="seeker-mobile-menu-btn"
                                    aria-label="Open menu"
                                >
                                    <Menu className="w-5 h-5" />
                                </button>
                            </SheetTrigger>
                            <SheetContent side="left" className="rounded-none w-72 p-0 flex flex-col">
                                <div className="border-b border-border px-5 py-4 flex items-center gap-2">
                                    <BrandLogo size={26} />
                                    <div className="flex flex-col leading-tight">
                                        <span className="font-display font-black tracking-tight text-base">{branding.short_name}</span>
                                        <span className="text-[10px] uppercase tracking-widest font-mono text-ikb">For candidates</span>
                                    </div>
                                </div>
                                <nav className="flex-1 overflow-y-auto py-2">
                                    {NAV.map((n) => {
                                        const active = loc.pathname.startsWith(n.to);
                                        const Icon = n.icon;
                                        return (
                                            <Link
                                                key={n.to}
                                                to={n.to}
                                                onClick={() => setMobileOpen(false)}
                                                data-testid={`mobile-seeker-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                                                className={`flex items-center gap-3 px-5 py-3 text-sm font-medium ${
                                                    active ? "bg-blue-50 text-ikb border-l-4 border-ikb" : "text-foreground hover:bg-secondary border-l-4 border-transparent"
                                                }`}
                                            >
                                                <Icon className="w-4 h-4" />
                                                {n.label}
                                            </Link>
                                        );
                                    })}
                                </nav>
                                <div className="border-t border-border p-4 space-y-2">
                                    <div className="text-xs font-mono text-muted-foreground truncate">{seeker.email}</div>
                                    <button
                                        onClick={() => { setMobileOpen(false); handleLogout(); }}
                                        className="w-full text-left text-sm flex items-center gap-2 py-2 hover:text-ikb"
                                        data-testid="mobile-seeker-logout"
                                    >
                                        <LogOut className="w-4 h-4" /> Sign out
                                    </button>
                                </div>
                            </SheetContent>
                        </Sheet>
                    )}

                    <Link to={seeker ? "/seeker/jobs" : "/"} className="flex items-center gap-2 min-w-0" data-testid="seeker-brand-link">
                        <BrandLogo size={28} />
                        <div className="flex flex-col leading-tight min-w-0">
                            <span className="font-display font-black tracking-tight text-sm sm:text-base truncate">{branding.short_name}</span>
                            <span className="hidden sm:block text-[10px] uppercase tracking-widest font-mono text-ikb">For candidates</span>
                        </div>
                    </Link>
                    {seeker && (
                        <nav className="hidden md:flex items-center gap-1">
                            {NAV.map((n) => {
                                const active = loc.pathname.startsWith(n.to);
                                const Icon = n.icon;
                                return (
                                    <Link
                                        key={n.to}
                                        to={n.to}
                                        data-testid={`seeker-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                                        className={`flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${
                                            active ? "text-ikb border-b-2 border-ikb -mb-px" : "text-muted-foreground hover:text-foreground"
                                        }`}
                                    >
                                        <Icon className="w-4 h-4" />
                                        {n.label}
                                    </Link>
                                );
                            })}
                        </nav>
                    )}
                    <div className="ml-auto flex items-center gap-2 sm:gap-3">
                        {seeker ? (
                            <>
                                <NotificationBell />
                                <div className="hidden lg:flex items-center gap-2 text-xs">
                                    <div className="w-8 h-8 bg-foreground text-background flex items-center justify-center font-mono text-xs font-bold">
                                        {(seeker.name || seeker.email).slice(0, 2).toUpperCase()}
                                    </div>
                                    <div className="leading-tight">
                                        <div className="font-medium">{seeker.name}</div>
                                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Candidate</div>
                                    </div>
                                </div>
                                <Button variant="outline" className="hidden md:inline-flex rounded-none h-9" onClick={handleLogout} data-testid="seeker-logout">
                                    <LogOut className="w-4 h-4 mr-2" /> Sign out
                                </Button>
                            </>
                        ) : (
                            <>
                                <Link to="/seeker/login" className="text-[11px] sm:text-xs font-mono uppercase tracking-widest border border-border px-2 sm:px-3 py-2 hover:bg-secondary" data-testid="seeker-login-link">Sign in</Link>
                                <Link to="/seeker/signup">
                                    <Button className="rounded-none bg-ikb hover:bg-[#001f7a] text-white h-9 px-3" data-testid="seeker-signup-link">
                                        <User className="w-4 h-4 mr-1.5" /> <span className="hidden sm:inline">Create account</span><span className="sm:hidden">Sign up</span>
                                    </Button>
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            </header>
            <main className="flex-1">{children}</main>
            <footer className="border-t border-border bg-white mt-10" data-testid="seeker-footer">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs">
                    <div className="flex items-center gap-2 text-muted-foreground">
                        <BrandLogo size={18} />
                        <span>© {new Date().getFullYear()} {branding.short_name} · For candidates</span>
                    </div>
                    <div className="flex items-center gap-4 flex-wrap">
                        {seeker && (
                            <Link to="/seeker/jobs" className="text-foreground hover:text-ikb" data-testid="footer-browse-jobs">Browse jobs</Link>
                        )}
                        {seeker && (
                            <Link to="/seeker/applications" className="text-foreground hover:text-ikb" data-testid="footer-my-apps">My applications</Link>
                        )}
                        {!seeker && (
                            <Link to="/seeker/jobs" className="text-foreground hover:text-ikb" data-testid="footer-browse-jobs">Browse jobs</Link>
                        )}
                        {!seeker && (
                            <Link to="/seeker/login" className="text-foreground hover:text-ikb" data-testid="footer-sign-in">Sign in</Link>
                        )}
                        {!seeker && (
                            <Link to="/seeker/signup" className="text-foreground hover:text-ikb" data-testid="footer-create-account">Create account</Link>
                        )}
                    </div>
                </div>
            </footer>
        </div>
    );
}
